import React, { useState, useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL = 10000; // 10 seconds

const GenerationQueuePanel = ({ isAdmin = false, companySlug, onComplete }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [queueData, setQueueData] = useState({ in_progress: [], pending: [] });
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const prevQueueDataRef = useRef({ in_progress: [], pending: [] });

  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await fetch(scopeUrl('/api/meta_ads_ai/reports/queue_status'), {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const prevData = prevQueueDataRef.current;
        const hadItems = prevData.in_progress.length > 0 || prevData.pending.length > 0;
        const hasItems = data.in_progress.length > 0 || data.pending.length > 0;

        setQueueData(data);
        setVisible(hasItems);
        prevQueueDataRef.current = data;

        // If queue just emptied and we had items before, notify parent
        if (hadItems && !hasItems && onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      console.error('[GenerationQueuePanel] Error fetching queue status:', err);
    }
  }, [onComplete]);

  // Poll for queue status
  useEffect(() => {
    fetchQueueStatus();
    pollRef.current = setInterval(fetchQueueStatus, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchQueueStatus]);

  // Public method to trigger refresh (called from ActionCable handler)
  const refresh = useCallback(() => {
    fetchQueueStatus();
  }, [fetchQueueStatus]);

  // Expose refresh via ref-like pattern on window for ActionCable integration
  useEffect(() => {
    window.__generationQueueRefresh = refresh;
    return () => { delete window.__generationQueueRefresh; };
  }, [refresh]);

  const handleCancel = async (reportId) => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel this generation? This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(scopeUrl(`/api/meta_ads_ai/reports/${reportId}/cancel`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });

      if (response.ok) {
        // Immediately refresh queue
        fetchQueueStatus();
      }
    } catch (err) {
      console.error('[GenerationQueuePanel] Cancel error:', err);
    }
  };

  if (!visible) return null;

  const { in_progress, pending } = queueData;

  return (
    <div className="generation-queue-panel">
      <div className="generation-queue-panel__header">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="generation-queue-panel__icon">
          <path d="M8 2L9.67 5.81L14 6.27L10.9 8.89L11.82 13L8 11.09L4.18 13L5.1 8.89L2 6.27L6.33 5.81L8 2Z"
            stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#fbbf24"/>
        </svg>
        <span className="generation-queue-panel__title">AI Generation Queue</span>
      </div>

      {in_progress.map(item => (
        <RunningItem
          key={item.id}
          item={item}
          isAdmin={isAdmin}
          onCancel={handleCancel}
        />
      ))}

      {pending.length > 0 && (
        <div className="generation-queue-panel__pending-section">
          <div className="generation-queue-panel__pending-label">QUEUED</div>
          {pending.map((item, idx) => (
            <PendingItem
              key={item.id}
              item={item}
              position={idx + 1}
              onCancel={handleCancel}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RunningItem = ({ item, isAdmin, onCancel }) => {
  const [elapsed, setElapsed] = useState(item.elapsed_seconds || 0);

  useEffect(() => {
    setElapsed(item.elapsed_seconds || 0);
    const timer = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [item.elapsed_seconds, item.id]);

  const formatElapsed = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  return (
    <div className="generation-queue-item generation-queue-item--running">
      <div className="generation-queue-item__main">
        <div className="generation-queue-item__status-indicator">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="31.416" strokeLinecap="round" opacity="0.3"/>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="15.708" strokeLinecap="round">
              <animate attributeName="stroke-dashoffset" values="0;-62.83" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
        <div className="generation-queue-item__info">
          <div className="generation-queue-item__event-name">{item.event_name || 'Unknown Event'}</div>
          <div className="generation-queue-item__details">
            <span className="generation-queue-item__stage">{item.stage_label}</span>
            <span className="generation-queue-item__separator">|</span>
            <span className="generation-queue-item__elapsed">{formatElapsed(elapsed)}</span>
            {isAdmin && item.estimated_cost && (
              <>
                <span className="generation-queue-item__separator">|</span>
                <span className="generation-queue-item__cost">{item.estimated_cost}<span className="admin-only-dot" /></span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          className="generation-queue-item__cancel"
          onClick={() => onCancel(item.id)}
          title="Cancel generation"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const PendingItem = ({ item, position, onCancel }) => {
  return (
    <div className="generation-queue-item generation-queue-item--pending">
      <div className="generation-queue-item__main">
        <div className="generation-queue-item__position">{position}</div>
        <div className="generation-queue-item__info">
          <div className="generation-queue-item__event-name">{item.event_name || 'Unknown Event'}</div>
        </div>
        <button
          type="button"
          className="generation-queue-item__cancel"
          onClick={() => onCancel(item.id)}
          title="Remove from queue"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default GenerationQueuePanel;
